import { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ReactComponent as FileCsvIcon } from '../../../../../../assets/icons/file-csv.svg';
import { ReactComponent as FileXlsxIcon } from '../../../../../../assets/icons/file-xlsx.svg';
import l from '../../../../../../lang';
import Dialog from '../../../../../common/dialog';
import DateRangePicker from '../../../../../common/date-range-picker';
import Loading from '../../../../../common/loading';
import MessageError from '../../../../../common/message-error';
import Message from '../../../../../common/message';
import { MessageTypes } from '../../../../../../constants';
import icDownloadFile from '../../../../../../assets/icons/download-file.svg?url';
import Select from '../../../../../common/select';
import { allCountries } from '../../../../../../config';
import { scrollIntoView } from '../../../../../../utils';
import ButtonLink from '../../../../../common/button-link';
import { usePayouts } from '../../../../../../store/payouts';
import { useDashboard } from '../../../../../../store/dashboard';
import { ExportType, Filters } from '../../../../../../store/interfaces';
import '../../../export/dialog/styles.scss';

const CONTACT_EXPORT_STATUSES = ['PENDING', 'PROCESSING', 'REJECTED', 'VERIFIED', 'EXPIRED', 'BLOCKED'];

const initialValues = (): Filters => {
  const now = new Date();
  const past = new Date(now.getTime() - 86400000 * 31);
  const pad = (value: number) => String(value).padStart(2, '0');

  return {
    created_from: `${past.getFullYear()}-${pad(past.getMonth() + 1)}-${pad(past.getDate())}`,
    created_to: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    expires_from: '',
    expires_to: '',
    status: '',
    country: '',
  };
};

const buildExportQuery = (filters: Filters) =>
  ['country', 'status', 'created_from', 'created_to', 'expires_from', 'expires_to']
    .filter(key => filters[key])
    .map(key => `${key}=${encodeURIComponent(filters[key])}`)
    .join('&');

const getDateRangeError = (from: string, to: string) => {
  if (!from || !to) {
    return '';
  }

  return new Date(from) > new Date(to) ? l('exportPayouts.invalidDates') : '';
};

const DialogExportPayoutContacts = () => {
  const {
    contactsExport: { loading, error, errorInfo, show, data, exportType },
    showExportPayoutContacts,
    exportPayoutContacts,
  } = usePayouts();
  const { merchantMe } = useDashboard();
  const messageRef = useRef<HTMLDivElement | null>(null);
  const [filter, setFilter] = useState<Filters>(initialValues());
  const [createdRangeError, setCreatedRangeError] = useState('');
  const [expiresRangeError, setExpiresRangeError] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const download = (fileData: any) => {
    if (exportType === ExportType.CSV) {
      const a = document.createElement('a');
      a.href = `data:text/csv;charset=utf-8,${escape(fileData)}`;
      a.download = `${l('menu.item.PAYOUT_CONTACTS')}-dLocalGo.csv`;
      a.click();
    }
    if (exportType === ExportType.XLSX) {
      const byteArray = new Uint8Array(fileData);
      const blob = new Blob([byteArray], { type: 'application/octetstream' });
      const url = window.URL || window.webkitURL;
      const link = url.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = link;
      a.download = `${l('menu.item.PAYOUT_CONTACTS')}-dLocalGo.xlsx`;
      a.click();
    }
  };

  useEffect(() => {
    if (show) {
      setFilter(initialValues());
      setCreatedRangeError('');
      setExpiresRangeError('');
    }
  }, [show]);

  useEffect(() => {
    if (error || data === '') {
      scrollIntoView(messageRef);
    }
    if (data && show) {
      download(data);
    }
  }, [error, data]);

  const countries = () => {
    const options: { [co: string]: string } = {};
    allCountries.forEach(co => {
      options[co] = l(`countryCode.${co}`);
    });
    return options;
  };

  const statuses = () =>
    CONTACT_EXPORT_STATUSES.reduce<Record<string, string>>((options, status) => {
      options[status] = l(`payoutContacts.status.${status}`);
      return options;
    }, {});

  const handleExport = (type: ExportType) => {
    if (!merchantMe?.id) {
      return;
    }
    setAnchorEl(null);
    exportPayoutContacts(String(merchantMe.id), buildExportQuery(filter), type);
  };

  return (
    <div className="export_transactions">
      <Dialog
        open={show}
        dividers={false}
        title={<span>{l('payoutContacts.export.title')}</span>}
        unpaddingContent
        unpaddingFooter
        type="sm"
        handleClose={() => showExportPayoutContacts(false)}
        body={
          <div>
            {loading && (
              <div className="export_transactions__loading">
                <Loading />
              </div>
            )}
            <div className={`export_transactions__content ${loading ? 'export_transactions__content__hide' : ''}`}>
              {!data && (
                <div>
                  <div>{l('payoutContacts.export.createdRange')}</div>
                  <div>
                    <DateRangePicker
                      error={createdRangeError}
                      fromDate={filter.created_from}
                      toDate={filter.created_to}
                      changeFrom={created_from => {
                        setCreatedRangeError(getDateRangeError(created_from, filter.created_to));
                        setFilter({ ...filter, created_from });
                      }}
                      changeTo={created_to => {
                        setCreatedRangeError(getDateRangeError(filter.created_from, created_to));
                        setFilter({ ...filter, created_to });
                      }}
                    />
                  </div>
                  <div className="export_transactions__other_filters">
                    <div>{l('payoutContacts.export.expiresRange')}</div>
                    <div>
                      <DateRangePicker
                        error={expiresRangeError}
                        fromDate={filter.expires_from}
                        toDate={filter.expires_to}
                        changeFrom={expires_from => {
                          setExpiresRangeError(getDateRangeError(expires_from, filter.expires_to));
                          setFilter({ ...filter, expires_from });
                        }}
                        changeTo={expires_to => {
                          setExpiresRangeError(getDateRangeError(filter.expires_from, expires_to));
                          setFilter({ ...filter, expires_to });
                        }}
                      />
                    </div>
                  </div>
                  <div className="export_transactions__other_filters">
                    <div>{l('exportPayouts.otherFilters')}</div>
                    <div>
                      <Select
                        name="filter-status"
                        label={l('payoutContacts.column.verificationStatus')}
                        defaultOptionText={l('common.all.m')}
                        defaultOptionEnabled
                        value={filter.status}
                        size="lg"
                        onChange={e => setFilter({ ...filter, status: e.target.value })}
                        options={statuses()}
                      />
                      <Select
                        name="filter-country"
                        size="lg"
                        label={l('payoutContacts.column.country')}
                        defaultOptionText={l('common.all.m')}
                        defaultOptionEnabled
                        value={filter.country}
                        onChange={e => setFilter({ ...filter, country: e.target.value })}
                        options={countries()}
                      />
                    </div>
                  </div>
                  <div ref={messageRef}>
                    {data === '' && (
                      <div className="export_transactions__empty">
                        <Message type={MessageTypes.WARNING} body={l('exportPayouts.empty')} />
                      </div>
                    )}
                    {error && (
                      <div className="export_transactions__error">
                        <MessageError errorInfo={errorInfo} />
                      </div>
                    )}
                  </div>
                </div>
              )}
              {data && (
                <div>
                  <div className="export_transactions__image_success">
                    <img src={icDownloadFile} className="export_transactions__image" alt="success" />
                  </div>
                  <p>{l('exportPayouts.successMessage')}</p>
                  <p>
                    <span>{l('exportPayouts.downloadAgain')}</span>
                    <ButtonLink text={l('exportPayouts.downloadAgain.here')} onClick={() => download(data)} />
                  </p>
                </div>
              )}
            </div>
            {!data && (
              <div className="export_transactions__export_btn">
                <Button
                  name="exportPayoutContacts"
                  variant="contained"
                  onClick={event => setAnchorEl(event.currentTarget)}
                  endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  disabled={!!createdRangeError || !!expiresRangeError || loading || !merchantMe?.id}
                >
                  {l('exportPayouts.exportBtn')}
                </Button>
                <Menu open={open} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
                  <MenuItem className="export_transactions__export_menu" onClick={() => handleExport(ExportType.CSV)}>
                    <FileCsvIcon />
                    CSV
                  </MenuItem>
                  <MenuItem className="export_transactions__export_menu" onClick={() => handleExport(ExportType.XLSX)}>
                    <FileXlsxIcon />
                    XLSX
                  </MenuItem>
                </Menu>
              </div>
            )}
          </div>
        }
      />
    </div>
  );
};

export default DialogExportPayoutContacts;
